import root from 'window-or-global';

const getHostPath = () => {
    let context = '';

    if (root.appContext && root.appContext !== '/') {
        context = root.appContext;
    }

    if (!root.location.origin) {
        const port = root.location.port ? `:${root.location.port}` : '';
        root.location.origin = `${root.location.protocol}//${root.location.hostname}${port}`;
    }

    return root.location.origin + context;
};

/** Build API path */
const getPathCallback = (origin: string) => (apiPath: string, apiVersion?: number) => {
    return typeof apiVersion === 'number'
        ? `${origin}/api/v${apiVersion}/${apiPath}`
        : `${origin}/api/${apiPath}`;
};

/**
 * Enum for URLs for the API endpoints.
 *
 */
export default function(apiVersion?: number) {
    /** Find origin path */
    const origin = getHostPath();
    const getFullPath = getPathCallback(origin);

    /** Build enum */
    const URL = {
        ORIGIN: `${origin}`,

        /**
         * APP
         */
        GET_APP_CONFIG: getFullPath('get-app-config'),
        GET_NEWS: getFullPath('get-news'),
        GET_ARTICLE: slug => getFullPath(`get-news/${slug}`),

        GET_OVERVIEW_DATA: getFullPath('get-overview'),
        GET_PAGE: getFullPath('get-page'),
        GET_ALERTS: getFullPath('get-alerts', apiVersion),

        VALIDATE_ACCOUNT: getFullPath('validate-account'),
        CREATE_SURVEY: getFullPath('surve'),

        /**
         * SOCIAL OAUTH
         */
        GET_OAUTH: getFullPath('get-oauth'),
        GET_OAUTH_LINK: getFullPath('get-oauth-link'),
        UPDATE_OAUTH_CHANNEL: getFullPath('update-oauth-channel'),

        /**
         * EULA
         */
        GET_CODE_OF_CONDUCT: getFullPath('get-eula/community_rules'),
        GET_AFFILIATE_AGREEMENT: getFullPath('get-eula/sac_agreement'),

        /**
         * EPIC ACCOUNT
         */
        GET_CHECK_SIGNUP_REQUIREMENTS_MET: getFullPath('check-signup-requirements-met'),
        CREATE_RESEND_EMAIL_VERIFICATION: getFullPath('resend-email-verification'),

        /**
         * AFFILIATE ACCOUNT
         */
        GET_INFLUENCER_PROFILE: getFullPath('get-affiliate-profile', 2),
        GET_CODE_AVAILABILITY: getFullPath('get-code-availability'),
        CREATE_INFLUENCER_PROFILE: getFullPath('create-affiliate-account', 2),
        UPDATE_INFLUENCER_PROFILE: getFullPath('update-affiliate-account'),
        REACTIVATE_INFLUENCER_PROFILE: getFullPath('reactivate-affiliate-account', 2),

        GET_AFFILIATE_SOCIAL_CHANNELS: getFullPath('get-social-channels'),
        UPDATE_AFFILIATE_MAIN_CHANNEL: getFullPath('update-main-channel', 2),
        DISCONNECT_AFFILIATE_SOCIAL_CHANNEL: getFullPath('disconnect-social-channel', 2),

        ACCEPT_AGREEMENT: getFullPath('accept-agreement', 2),
        CLOSE_ACCOUNT: getFullPath('close-affiliate-account', 2),

        GET_PRODUCTS: getFullPath('get-products', apiVersion),
        GET_EARNED_PRODUCTS: getFullPath('get-earned-products', apiVersion),
        GET_EARNINGS_DATA: getFullPath('get-earnings-data', apiVersion),

        /**
         * TAX and PAYOUT
         */
        GET_FINAL_PAYOUT: getFullPath('get-final-payout'),
        GET_PAYOUT_ACCOUNT: getFullPath('get-payout-account'),
        CREATE_PAYOUT_ACCOUNT: getFullPath('create-payout-account'),
        UPDATE_PAYOUT_ACCOUNT: getFullPath('update-payout-account'),

        GET_TAX_STATUS: getFullPath('get-tax-status'),
        GET_TAX_INTERVIEW: getFullPath('get-tax-interview'),
        GET_TAX_PROFILE: getFullPath('get-tax-profile'),
        GET_TAX_RECORD: getFullPath('get-tax-record'),
        GET_RECORD_EMAIL: getFullPath('get-record-email'),
        UPDATE_TAX_RECORD: getFullPath('update-tax-record'),
        RETAKE_INTERVIEW: getFullPath('retake-tax-interview'),
        REFRESH_TAX_RECORD: getFullPath('refresh-tax-record'),
        GET_HYPERWALLET_ACTIVE_LINK: getFullPath('get-hyperwallet-link'),

        GET_PAYOUT_ACCOUNT_STATUS: getFullPath('get-account-status'),
        GET_PAYOUT_REPORT_LIST: getFullPath('get-report-list', 2),
        GET_PAYOUT_REPORT_DETAIL: getFullPath('get-report-detail', 2),
        DOWNLOAD_PAYOUT_REPORT_DETAIL: getFullPath('download-report-detail', 2),

        GET_CONSENT: getFullPath('get-payout-consent'),
        CONSENT_FOR_PAYMENT: getFullPath('consent-for-payment'),

        GET_PAYOUT_ACCEPTANCE: getFullPath('get-payout-acceptance'),
        GET_PAYABLE_AMOUNTS: getFullPath('get-payable-amounts', 2),
        GET_LAST_PAYMENT: getFullPath('get-last-payment'),
        GET_BALANCE_PAYABLE: getFullPath('get-balance-payable'),
        GET_UPDATE_AVAILABLE: getFullPath('get-update-available'),

        GET_OFFLINE_PAYMENT: getFullPath('get-offline-payment', 2),
        SAVE_OFFLINE_PAYMENT: getFullPath('save-offline-payment', 2),

        GET_ORGANIZATIONS: getFullPath('get-organizations', 2),

        UPLOAD_W9FORM: getFullPath('upload-w9form'),
        CHECK_W9FORM: getFullPath('check-w9form'),

        GET_PAYOUT_REPORT: getFullPath('get-payout-report'),
        DOWNLOAD_PAYOUT_REPORT: getFullPath('download-payout-report'),

        TRACE_SECURITY_LINK: getFullPath('trace-security-link', 2),

        /**
         * FORTNITE GAMES
         */
        GET_TOTAL_ACTIVE_USERS: getFullPath('total-active-users'),
        GET_TOTAL_LIFETIME_PLAYS: getFullPath('total-lifetime-plays'),
        GET_TOTAL_LIFETIME_PLAYS_OVERVIEW: value =>
            getFullPath(`total-lifetime-plays-overview/${value}`),
        GET_TOTAL_ACTIVE_USERS_OVERVIEW: value =>
            getFullPath(`total-active-users-overview/${value}`),
        GET_GAMES_LIST: getFullPath('games'),
        GET_AVERAGE_PLAY_TIME: getFullPath('average-play-time'),
        GET_RETURN_PLAYERS: getFullPath('return-players'),

        GET_HIGHLIGHTS: value => getFullPath(`overall-highlights/${value}`),

        GET_TOP_PERFORMING_GAMES: value => getFullPath(`top-performing-games/${value}`),

        GET_TOP_10_GAMES: value => getFullPath(`top-10-games/${value}`),

        GET_PERFORMANCE: getFullPath('performance'),
        GET_REWARDS: getFullPath('rewards'),
        GET_REWARDS_TOP_10_GAMES: getFullPath('rewards-top-10-games'),
        GET_GAME_XP: getFullPath('game-xp'),
        GET_GAME_XP_HIGHLIGHTS: getFullPath('game-xp-highlights'),
        GET_PLAYRATE: getFullPath('playrate'),

        SET_FG_ONBOARDING_COMPLETE: getFullPath('fg-onboarding-complete'),
        SET_TIME_RANGE_WARNING_STATE: getFullPath('time-range-warning-state')
    };

    return URL;
}
